// Typed client + query hooks for the P5-C admin API (packages/domain/{order,payment,customer,
// promotion,analytics,checkout}). Shapes mirror the server presenters. Every key carries the org and
// store IDs, so one store's cache never answers for another.
import { useQuery } from "@tanstack/react-query";
import { request } from "../api/client";

export type OrderStatus =
  | "draft"
  | "pending"
  | "confirmed"
  | "processing"
  | "ready"
  | "shipped"
  | "delivered"
  | "completed"
  | "cancelled"
  | "returned";
/** The orders list filter (GET …/orders query): view, search, period (placed_at ms) and filters. */
export interface OrderListFilter {
  view: OrderView;
  q?: string;
  logistics?: LogisticsState | null;
  from?: number;
  to?: number;
  status?: OrderStatus;
  payment_status?: PaymentStatus;
  payment_method?: string;
  channel?: string;
}

/** Where an order's parcel is (orders.logistics_state = the shipment state). */
export const LOGISTICS_STATES = [
  "booked",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "failed_attempt",
  "rto",
  "delivered",
  "cancelled",
] as const;
export type LogisticsState = (typeof LOGISTICS_STATES)[number];
export type PaymentStatus =
  | "unpaid"
  | "partial"
  | "paid"
  | "refunded"
  | "partially_refunded"
  | "awaiting_verification"
  | "overpaid";
export const ORDER_VIEWS = [
  "all",
  "to-confirm",
  "awaiting-verification",
  "unfulfilled-48h",
  "stuck-dispatch",
  "overpaid",
  "paid-out-of-stock",
  "unconfirmed-cod",
] as const;
export type OrderView = (typeof ORDER_VIEWS)[number];

export interface OrderSummary {
  id: string;
  number: string;
  channel: string;
  status: OrderStatus;
  payment_status: PaymentStatus;
  payment_method: string;
  customer_id: string | null;
  contact: { name: string; phone: string; email: string | null };
  district: string | null;
  total_paisa: number;
  due_paisa: number;
  flags: string[];
  /** Merchant labels (PATCH …/orders/{id} `tags`), shown as coloured chips in the list. */
  tags?: string[];
  /** The courier and where its parcel is (kept by the automatic courier check). */
  courier?: string | null;
  logistics_state?: LogisticsState | null;
  sla: { due_at: string | null; breached: boolean } | null;
  is_test: boolean;
  placed_at: string;
  updated_at?: string;
  version: number;
}

export const LABEL_COLORS = [
  "orange",
  "amber",
  "mint",
  "green",
  "sky",
  "blue",
  "gray",
  "red",
  "pink",
  "purple",
] as const;
export type LabelColor = (typeof LABEL_COLORS)[number];
/** A store order label (GET/PUT …/order-labels); orders carry the name in `tags`. */
export interface OrderLabel {
  name: string;
  color: LabelColor;
}

export type OrderSource =
  "instagram" | "facebook" | "tiktok" | "whatsapp" | "phone" | "walk_in" | "other";
export type ManualPaymentMethod =
  "cod" | "cash" | "bank_transfer" | "merchant_qr" | "esewa" | "khalti" | "other";
/** Body of `POST …/orders/manual` (POS-02, packages/domain/pos/src/http.ts ManualBody). */
export interface ManualOrderRequest {
  lines: { variant_id: string; qty: number }[];
  contact: { name: string; phone: string };
  address: {
    province: string;
    district: string;
    local_level: string;
    /** Optional on back-office orders; the admin asks for the district instead. */
    ward?: number;
    tole: string;
    landmark?: string;
  };
  service: "standard" | "express" | "pickup";
  source: OrderSource;
  payment: {
    method: ManualPaymentMethod;
    status: "unpaid" | "paid" | "partial";
    paid_paisa: number;
  };
  note?: string;
}

export interface OrderDetail extends OrderSummary {
  phone_alt: string | null;
  address: {
    /** Area codes (GEO-001); masked orders may omit them. */
    province?: string | null;
    district?: string | null;
    local_level?: string | null;
    province_name: string | null;
    district_name: string | null;
    local_level_name: string | null;
    ward: number | null;
    tole: string | null;
    landmark: string | null;
  };
  delivery_service: string | null;
  totals: {
    subtotal: number;
    discount: number;
    delivery: number;
    surcharge: number;
    tax: number;
    total: number;
    paid: number;
    refunded: number;
    due: number;
    tax_inclusive: boolean;
  };
  coupon_codes: string[];
  customer_note: string | null;
  lines: {
    id: string;
    title: string;
    variant_title: string;
    sku?: string | null;
    qty: number;
    unit_price_paisa: number;
    line_total_paisa: number;
    /** 1022: answers + installation added to this line (inside line_total_paisa). */
    surcharge_paisa?: number;
    extras?: {
      display: { field_id: string; label: string; value: string }[];
      installation: { preset_id: string; name: string; price_paisa: number } | null;
    } | null;
  }[];
  holds: { product_id: string; state: "held" | "committed" | "released" | "restocked" }[];
  payments: {
    id: string;
    method: string;
    amount_paisa: number;
    status: string;
    is_manual: boolean;
    reason: string | null;
    created_at: string;
  }[];
  proofs: {
    id: string;
    state: string;
    amount_paisa: number;
    reference_code: string | null;
    created_at: string;
  }[];
  timeline: {
    seq: number;
    type: string;
    actor_type: string;
    data: Record<string, unknown> | null;
    at: string;
  }[];
  allowed_transitions: OrderStatus[];
  editable: boolean;
}

export interface QueueItem {
  id: string;
  order_id: string;
  order_number: string;
  customer_name: string;
  asset_id: string;
  reference_code: string | null;
  amount_paisa: number;
  submitted_at: string;
  waiting_minutes: number;
}

export interface PaymentRecord {
  id: string;
  order_id: string;
  order_number: string;
  method: string;
  amount_paisa: number;
  status: "pending" | "succeeded" | "failed" | "reversed";
  is_manual: boolean;
  created_at: string;
}

export interface MethodStatus {
  provider: "cod" | "manual_qr" | "fonepay_qr" | "fonepay_web" | "esewa" | "khalti" | "stripe";
  enabled: boolean;
  state: "pending_activation" | "active" | "error";
  reason:
    | "gateway_phase_6"
    | "credentials_missing"
    | "spec_required"
    | "no_qr_account"
    | "disabled"
    | null;
}

export interface QrAccount {
  id: string;
  label: string;
  kind: "wallet" | "bank";
  account_name: string;
  qr_asset_id: string | null;
  instructions: string | null;
  active: boolean;
  version: number;
}

export interface Customer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  orders_count: number;
  ltv_paisa: number;
  last_order_at: string | null;
  district: string | null;
  is_banned: boolean;
  /** Free labels (up to 20), e.g. "wholesale", "VIP". */
  tags?: string[];
  /** A private note for staff. */
  note?: string | null;
  created_at?: string;
  version: number;
}

export interface CustomerWrite {
  name?: string;
  phone?: string;
  email?: string;
  note?: string | null;
  tags?: string[];
}

export interface Coupon {
  id: string;
  code: string;
  type: "percent" | "fixed" | "free_delivery" | "delivery_discount";
  value: number;
  limit_total: number | null;
  limit_per_customer: number | null;
  starts_at: string | null;
  ends_at: string | null;
  active: boolean;
  uses: number;
  version: number;
  /** Largest discount for a percent coupon (paisa). */
  cap_paisa?: number | null;
  conditions?: { min_order_paisa?: number; methods?: CouponMethod[] };
  /** Applied at checkout without typing the code. */
  auto_apply?: boolean;
  /** Can be used together with other coupons. */
  stacking?: boolean;
}
export type CouponMethod = "cod" | "manual_qr" | "fonepay_qr" | "fonepay_web" | "esewa" | "khalti";

export interface TaxSettings {
  vat_registered: boolean;
  prices_include_vat: boolean;
  delivery_taxable: boolean;
  rates: { standard: number; exempt: number; zero: number };
  version: number;
}

export interface Kpis {
  period: "today" | "7d" | "30d";
  orders: number;
  cancelled: number;
  sales_paisa: number;
  average_order_paisa: number;
  collected_paisa: number;
  outstanding_paisa: number;
  queue: {
    to_confirm: number;
    awaiting_verification: number;
    unfulfilled_late: number;
    unconfirmed_cod: number;
  };
  previous: { orders: number; sales_paisa: number };
  series: { date: string; orders: number; sales_paisa: number }[];
  hourly: { hour: number; orders: number; sales_paisa: number }[];
}

interface Page<T> {
  data: T[];
  next_cursor: string | null;
}

const s = (orgId: string, storeId: string) =>
  `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}`;

export const commerceApi = {
  orders: (o: string, st: string, q: OrderListFilter & { cursor?: string }) =>
    request<Page<OrderSummary>>("GET", `${s(o, st)}/orders`, {
      query: {
        view: q.view,
        q: q.q || undefined,
        cursor: q.cursor,
        logistics: q.logistics ?? undefined,
        from: q.from,
        to: q.to,
        status: q.status,
        payment_status: q.payment_status,
        payment_method: q.payment_method,
        channel: q.channel,
      },
    }),
  views: (o: string, st: string) =>
    request<{ data: { view: OrderView; count: number }[] }>("GET", `${s(o, st)}/order-views`),
  order: (o: string, st: string, id: string) =>
    request<OrderDetail>("GET", `${s(o, st)}/orders/${encodeURIComponent(id)}`),
  transition: (
    o: string,
    st: string,
    id: string,
    to: OrderStatus,
    reason: string | undefined,
    key: string,
  ) =>
    request<OrderSummary>("POST", `${s(o, st)}/orders/${encodeURIComponent(id)}/transitions`, {
      body: reason ? { to, reason } : { to },
      idempotencyKey: key,
    }),
  labels: (o: string, st: string) =>
    request<{ data: OrderLabel[] }>("GET", `${s(o, st)}/order-labels`),
  saveLabels: (o: string, st: string, labels: OrderLabel[]) =>
    request<{ data: OrderLabel[] }>("PUT", `${s(o, st)}/order-labels`, { body: { labels } }),
  setTags: (o: string, st: string, order: Pick<OrderSummary, "id" | "version">, tags: string[]) =>
    request<OrderSummary>("PATCH", `${s(o, st)}/orders/${encodeURIComponent(order.id)}`, {
      body: { tags },
      ifMatch: order.version,
    }),
  /** POS-02 back-office order: the server prices the lines and quotes delivery. */
  createManual: (o: string, st: string, body: ManualOrderRequest, key: string) =>
    request<{ id: string; number: string; channel: "manual"; totals: { total: number } }>(
      "POST",
      `${s(o, st)}/orders/manual`,
      { body, idempotencyKey: key },
    ),
  manualPayment: (
    o: string,
    st: string,
    id: string,
    body: { amount_paisa: number; method: string; reason: string },
    key: string,
  ) =>
    request<{ id: string }>(
      "POST",
      `${s(o, st)}/orders/${encodeURIComponent(id)}/payments/manual`,
      { body, idempotencyKey: key },
    ),
  queue: (o: string, st: string) =>
    request<Page<QueueItem>>("GET", `${s(o, st)}/payments/verification-queue`),
  payments: (o: string, st: string) => request<Page<PaymentRecord>>("GET", `${s(o, st)}/payments`),
  verify: (o: string, st: string, proofId: string, reason: string, key: string) =>
    request<unknown>("POST", `${s(o, st)}/payments/${encodeURIComponent(proofId)}/verify`, {
      body: { reason },
      idempotencyKey: key,
    }),
  reject: (o: string, st: string, proofId: string, reason: string, key: string) =>
    request<unknown>("POST", `${s(o, st)}/payments/${encodeURIComponent(proofId)}/reject`, {
      body: { reason },
      idempotencyKey: key,
    }),
  methods: (o: string, st: string) =>
    request<{ data: MethodStatus[] }>("GET", `${s(o, st)}/payment-methods`),
  setMethod: (o: string, st: string, provider: string, enabled: boolean) =>
    request<{ data: MethodStatus[] }>("PUT", `${s(o, st)}/payment-methods/${provider}`, {
      body: { enabled },
    }),
  qrAccounts: (o: string, st: string) =>
    request<Page<QrAccount>>("GET", `${s(o, st)}/manual-qr-accounts`),
  addQrAccount: (
    o: string,
    st: string,
    body: {
      label: string;
      kind: "wallet" | "bank";
      account_name: string;
      instructions?: string;
      qr_asset_id?: string;
    },
    key: string,
  ) => request<QrAccount>("POST", `${s(o, st)}/manual-qr-accounts`, { body, idempotencyKey: key }),
  /** Sets or replaces the account's QR image (an uploaded media asset). */
  setQrImage: (o: string, st: string, a: Pick<QrAccount, "id" | "version">, assetId: string) =>
    request<QrAccount>("PATCH", `${s(o, st)}/manual-qr-accounts/${encodeURIComponent(a.id)}`, {
      body: { qr_asset_id: assetId },
      ifMatch: a.version,
    }),
  customers: (o: string, st: string, q: string, cursor?: string) =>
    request<Page<Customer>>("GET", `${s(o, st)}/customers`, {
      query: { q: q || undefined, cursor },
    }),
  createCustomer: (o: string, st: string, body: CustomerWrite, key: string) =>
    request<Customer>("POST", `${s(o, st)}/customers`, { body, idempotencyKey: key }),
  /** Name, note and tags; phone and email change only through orders (server rule). */
  updateCustomer: (
    o: string,
    st: string,
    c: Pick<Customer, "id" | "version">,
    body: CustomerWrite,
  ) =>
    request<Customer>("PATCH", `${s(o, st)}/customers/${encodeURIComponent(c.id)}`, {
      body,
      ifMatch: c.version,
    }),
  customer: (o: string, st: string, id: string) =>
    request<Customer & { orders: OrderSummary[] }>(
      "GET",
      `${s(o, st)}/customers/${encodeURIComponent(id)}`,
    ),
  coupons: (o: string, st: string) => request<Page<Coupon>>("GET", `${s(o, st)}/coupons`),
  createCoupon: (o: string, st: string, body: Record<string, unknown>, key: string) =>
    request<Coupon>("POST", `${s(o, st)}/coupons`, { body, idempotencyKey: key }),
  updateCoupon: (o: string, st: string, c: Coupon, body: Record<string, unknown>) =>
    request<Coupon>("PATCH", `${s(o, st)}/coupons/${encodeURIComponent(c.id)}`, {
      body,
      ifMatch: c.version,
    }),
  deleteCoupon: (o: string, st: string, id: string) =>
    request<void>("DELETE", `${s(o, st)}/coupons/${encodeURIComponent(id)}`),
  tax: (o: string, st: string) => request<TaxSettings>("GET", `${s(o, st)}/settings/tax`),
  saveTax: (
    o: string,
    st: string,
    version: number,
    body: Partial<Omit<TaxSettings, "rates" | "version">>,
  ) => request<TaxSettings>("PATCH", `${s(o, st)}/settings/tax`, { body, ifMatch: version }),
  kpis: (o: string, st: string, period: Kpis["period"]) =>
    request<Kpis>("GET", `${s(o, st)}/analytics/kpis`, { query: { period } }),
  unfinished: (o: string, st: string) =>
    request<Page<UnfinishedCheckout>>("GET", `${s(o, st)}/checkouts/unfinished`),
};

/** An open cart with a delivery quote, untouched for 30+ minutes (`GET …/checkouts/unfinished`). */
export interface UnfinishedCheckout {
  id: string;
  kind: "cart" | "buy_now" | "link";
  lines: number;
  customer_id: string | null;
  contact_consented: boolean;
  updated_at: string;
}

export const commerceKeys = {
  all: (o: string, st: string) => ["commerce", o, st] as const,
  orders: (o: string, st: string, f: OrderListFilter) =>
    [
      "commerce",
      o,
      st,
      "orders",
      f.view,
      f.q ?? "",
      JSON.stringify({ ...f, view: undefined, q: undefined }),
    ] as const,
  views: (o: string, st: string) => ["commerce", o, st, "order-views"] as const,
  order: (o: string, st: string, id: string) => ["commerce", o, st, "order", id] as const,
  queue: (o: string, st: string) => ["commerce", o, st, "queue"] as const,
  payments: (o: string, st: string) => ["commerce", o, st, "payments"] as const,
  methods: (o: string, st: string) => ["commerce", o, st, "methods"] as const,
  qr: (o: string, st: string) => ["commerce", o, st, "qr"] as const,
  customers: (o: string, st: string, q: string) => ["commerce", o, st, "customers", q] as const,
  customer: (o: string, st: string, id: string) => ["commerce", o, st, "customer", id] as const,
  coupons: (o: string, st: string) => ["commerce", o, st, "coupons"] as const,
  tax: (o: string, st: string) => ["commerce", o, st, "tax"] as const,
  kpis: (o: string, st: string, p: string) => ["commerce", o, st, "kpis", p] as const,
  unfinished: (o: string, st: string) => ["commerce", o, st, "unfinished"] as const,
  labels: (o: string, st: string) => ["commerce", o, st, "order-labels"] as const,
};

export function useOrders(o: string, st: string, f: OrderListFilter, enabled: boolean) {
  return useQuery({
    queryKey: commerceKeys.orders(o, st, f),
    queryFn: () => commerceApi.orders(o, st, f),
    enabled,
  });
}
export function useOrderViews(o: string, st: string, enabled: boolean) {
  return useQuery({
    queryKey: commerceKeys.views(o, st),
    queryFn: () => commerceApi.views(o, st),
    enabled,
  });
}
export function useOrder(o: string, st: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: commerceKeys.order(o, st, id),
    queryFn: () => commerceApi.order(o, st, id),
    enabled,
  });
}
export function useKpis(o: string, st: string, period: Kpis["period"], enabled: boolean) {
  return useQuery({
    queryKey: commerceKeys.kpis(o, st, period),
    queryFn: () => commerceApi.kpis(o, st, period),
    enabled,
  });
}

export function useOrderLabels(o: string, st: string, enabled = true) {
  return useQuery({
    queryKey: commerceKeys.labels(o, st),
    queryFn: () => commerceApi.labels(o, st),
    enabled,
  });
}
